import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight, ChevronRight, FileText, Search, Download,
  UserPlus, UserMinus, Key, Laptop, Shield, RefreshCw,
  CheckCircle, Clock, AlertTriangle
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { MarketingLayout } from '../../components/marketing/MarketingLayout';

const templates = [
  {
    id: 'employee-onboarding',
    name: 'Employee Onboarding',
    description: 'Complete new hire onboarding including identity provisioning, group assignments, and device enrollment.',
    category: 'JML',
    icon: UserPlus,
    actions: 12,
    approvals: 2,
    estimatedTime: '< 4 hours',
    systems: ['Okta', 'Azure AD', 'BambooHR', 'Slack', 'Intune'],
    popular: true
  },
  {
    id: 'employee-offboarding',
    name: 'Employee Offboarding',
    description: 'Secure offboarding with access revocation, device wipe, and audit trail generation.',
    category: 'JML',
    icon: UserMinus,
    actions: 15,
    approvals: 1,
    estimatedTime: '< 2 hours',
    systems: ['Okta', 'Azure AD', 'Slack', 'Google Workspace', 'Intune'],
    popular: true
  },
  {
    id: 'role-change',
    name: 'Role Change / Transfer',
    description: 'Update access based on new role. Add new permissions, remove old ones, update group memberships.',
    category: 'JML',
    icon: RefreshCw,
    actions: 8,
    approvals: 2,
    estimatedTime: '< 1 day',
    systems: ['Okta', 'Azure AD', 'BambooHR']
  },
  {
    id: 'access-request',
    name: 'Standard Access Request',
    description: 'Request access to applications or resources with manager approval.',
    category: 'Access',
    icon: Key,
    actions: 4,
    approvals: 1,
    estimatedTime: '< 1 hour',
    systems: ['Okta', 'Azure AD', 'Slack'],
    popular: true
  },
  {
    id: 'elevated-access',
    name: 'Elevated Access Request',
    description: 'Request temporary admin or elevated privileges with security review.',
    category: 'Access',
    icon: Shield,
    actions: 6,
    approvals: 3,
    estimatedTime: '< 4 hours',
    systems: ['Okta', 'Azure AD', 'AWS IAM']
  },
  {
    id: 'device-enrollment',
    name: 'Device Enrollment',
    description: 'Enroll new devices into MDM with compliance policies applied.',
    category: 'Endpoints',
    icon: Laptop,
    actions: 5,
    approvals: 0,
    estimatedTime: '< 30 min',
    systems: ['Intune', 'Jamf']
  },
  {
    id: 'device-replacement',
    name: 'Device Replacement',
    description: 'Replace lost or damaged device with secure data wipe and new device setup.',
    category: 'Endpoints',
    icon: Laptop,
    actions: 8,
    approvals: 1,
    estimatedTime: '< 1 day',
    systems: ['Intune', 'Jamf', 'Okta']
  },
  {
    id: 'security-incident',
    name: 'Security Incident Response',
    description: 'Contain compromised account with session termination and MFA reset.',
    category: 'Security',
    icon: AlertTriangle,
    actions: 6,
    approvals: 0,
    estimatedTime: '< 15 min',
    systems: ['Okta', 'Azure AD', 'CrowdStrike']
  },
  {
    id: 'contractor-onboarding',
    name: 'Contractor Onboarding',
    description: 'Limited access onboarding for contractors with time-bound permissions.',
    category: 'JML',
    icon: UserPlus,
    actions: 8,
    approvals: 2,
    estimatedTime: '< 2 hours',
    systems: ['Okta', 'Azure AD', 'Slack']
  },
  {
    id: 'access-review',
    name: 'Quarterly Access Review',
    description: 'Manager attestation of direct reports access with automatic revocation.',
    category: 'Compliance',
    icon: CheckCircle,
    actions: 3,
    approvals: 1,
    estimatedTime: 'Varies',
    systems: ['Okta', 'Azure AD']
  }
];

const categories = ['All', 'JML', 'Access', 'Endpoints', 'Security', 'Compliance'];

export const Templates = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');

  const filteredTemplates = useMemo(() => {
    return templates.filter(t => {
      const matchesSearch = t.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        t.description.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesCategory = selectedCategory === 'All' || t.category === selectedCategory;
      return matchesSearch && matchesCategory;
    });
  }, [searchQuery, selectedCategory]);

  return (
    <MarketingLayout>
      <div className="min-h-screen">
        {/* Hero */}
        <section className="py-16 px-6 bg-white border-b border-border">
          <div className="max-w-5xl mx-auto">
            <div className="flex items-center gap-2 text-sm text-muted-foreground mb-4">
              <Link to="/" className="hover:text-foreground">Home</Link>
              <ChevronRight className="w-4 h-4" />
              <span>Templates</span>
            </div>
            <div className="flex items-start gap-6">
              <div className="w-14 h-14 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                <FileText className="w-7 h-7 text-primary" />
              </div>
              <div className="flex-1">
                <h1 className="text-3xl md:text-4xl font-semibold text-foreground mb-4 tracking-tight">
                  Workflow Templates
                </h1>
                <p className="text-lg text-muted-foreground max-w-3xl leading-relaxed">
                  Pre-built action bundles for common IT operations. Customize and deploy in minutes.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Filters */}
        <section className="py-6 px-6 bg-secondary/30 border-b border-border">
          <div className="max-w-5xl mx-auto">
            <div className="flex flex-col md:flex-row items-start md:items-center gap-4">
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <input
                  type="text"
                  placeholder="Search templates..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-9 pr-4 py-2 bg-white border border-border rounded-lg text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                />
              </div>
              <div className="flex items-center gap-2">
                {categories.map((cat) => (
                  <button
                    key={cat}
                    onClick={() => setSelectedCategory(cat)}
                    className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
                      selectedCategory === cat
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-white text-muted-foreground hover:text-foreground border border-border'
                    }`}
                  >
                    {cat}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Templates Grid */}
        <section className="py-16 px-6 bg-white">
          <div className="max-w-5xl mx-auto">
            {filteredTemplates.length === 0 ? (
              <div className="text-center py-12">
                <FileText className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-foreground mb-2">No templates found</h3>
                <p className="text-muted-foreground mb-4">Try adjusting your search or filter</p>
                <Button variant="outline" onClick={() => { setSearchQuery(''); setSelectedCategory('All'); }}>
                  Clear filters
                </Button>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 gap-6">
                {filteredTemplates.map((template) => (
                  <div
                    key={template.id}
                    className="bg-secondary/30 border border-border rounded-lg p-6 hover:border-primary/50 transition-colors"
                  >
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                          <template.icon className="w-5 h-5 text-primary" />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <h3 className="text-base font-semibold text-foreground">{template.name}</h3>
                            {template.popular && (
                              <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium bg-primary/10 text-primary rounded">
                                Popular
                              </span>
                            )}
                          </div>
                          <span className="text-xs text-muted-foreground">{template.category}</span>
                        </div>
                      </div>
                    </div>
                    <p className="text-sm text-muted-foreground mb-4">{template.description}</p>
                    <div className="flex items-center gap-4 text-xs text-muted-foreground mb-4">
                      <span className="flex items-center gap-1">
                        <CheckCircle className="w-3 h-3" />
                        {template.actions} actions
                      </span>
                      <span className="flex items-center gap-1">
                        <Shield className="w-3 h-3" />
                        {template.approvals} approvals
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {template.estimatedTime}
                      </span>
                    </div>
                    <div className="flex flex-wrap gap-1 mb-4">
                      {template.systems.map((sys, idx) => (
                        <span key={idx} className="inline-flex items-center px-2 py-0.5 bg-white text-xs text-muted-foreground rounded border border-border">
                          {sys}
                        </span>
                      ))}
                    </div>
                    <div className="flex items-center gap-2 pt-4 border-t border-border">
                      <Button variant="outline" size="sm" className="flex-1">
                        <Download className="w-4 h-4 mr-2" />
                        Download
                      </Button>
                      <Button size="sm" className="flex-1">
                        View Details
                        <ArrowRight className="w-4 h-4 ml-2" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>

        {/* Custom Templates CTA */}
        <section className="py-16 px-6 bg-secondary/30 border-t border-border">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-2xl font-semibold text-foreground mb-4">Need a Custom Template?</h2>
            <p className="text-muted-foreground mb-8">
              Our team can help you build templates specific to your organization's workflows and systems.
            </p>
            <div className="flex justify-center gap-3">
              <Button asChild size="lg">
                <Link to="/demo/request">
                  Request Custom Template
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Link>
              </Button>
              <Button asChild size="lg" variant="outline">
                <Link to="/docs">View Documentation</Link>
              </Button>
            </div>
          </div>
        </section>
      </div>
    </MarketingLayout>
  );
};

export default Templates;
